import { Injectable, inject } from '@angular/core';
import { Ability, subject } from '@casl/ability';
import { firstValueFrom } from 'rxjs';
import { ProjectDrawingsService } from './project-drawings.service';
import { AuthService } from './auth.service';

/** How long a "no current sheets" (or no drawing access) answer is trusted. */
const NO_DRAWINGS_TTL_MS = 5 * 60 * 1000;

/**
 * Whether a project has published drawing sheets the user can read (drawings slice 3): one `GET …/drawings/sheets?
 * status=current&total=1`, cached per account + project like DesignContextAvailabilityService. The location picker
 * opens the ArcGIS picker (with its Drawing tab) for such a project even without a published map catalog.
 */
@Injectable({ providedIn: 'root' })
export class DrawingsAvailabilityService {
  private drawings = inject(ProjectDrawingsService);
  private auth = inject(AuthService);
  private ability = inject(Ability, { optional: true });
  private cache = new Map<string, { answer: Promise<boolean>; expiresAt: number }>();

  available(projectId: string): Promise<boolean> {
    if (!projectId) return Promise.resolve(false);
    // No drawing read: no request (the picker would not show the tab anyway).
    if (this.ability && !this.ability.can('read', subject('ProjectDrawing', { project_id: projectId }))) return Promise.resolve(false);
    const key = `${this.auth.getCurrentUser()?.id || ''}:${projectId}`;
    const cached = this.cache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.answer;
    const entry = { answer: null as Promise<boolean>, expiresAt: Infinity };
    entry.answer = firstValueFrom(this.drawings.listSheets(projectId, { status: 'current', start: 0, total: 1 }))
      .then(({ data, totalRecords }) => {
        const found = totalRecords > 0 || data.length > 0;
        // A project gains sheets when a set is published mid-session: a "no" is trusted only briefly.
        if (!found && this.cache.get(key) === entry) entry.expiresAt = Date.now() + NO_DRAWINGS_TTL_MS;
        return found;
      })
      .catch(error => {
        if (this.cache.get(key) === entry) {
          // No access (403) or no project (404): remember briefly. Transient failures retry next time.
          if (error?.status === 403 || error?.status === 404) entry.expiresAt = Date.now() + NO_DRAWINGS_TTL_MS;
          else this.cache.delete(key);
        }
        return false;
      });
    this.cache.set(key, entry);
    return entry.answer;
  }
}
